// Raw legacy URLs and warm-cache cards feed the same native renderers.
import { pick, str } from './read';
import { resolvedStatusEntry } from './resolve-refs';
import { renderStatusModifyForm } from './render-status-form';
import { buildStatusEffectCard } from './render-status-effect';
import type { EditCtx, StatusEntry, StatusTemplateArr } from './types';

type RecordValue = Record<string, unknown>;
const record = (value: unknown): RecordValue | undefined =>
    value !== null && typeof value === 'object' && !Array.isArray(value) ? value as RecordValue : undefined;

type ObjectBridge = Window & {
    fetchAndCacheObject?: (id: string, opts?: { authoredCard?: boolean }) => Promise<RecordValue | null>;
};

async function definition(entry: StatusEntry): Promise<StatusEntry> {
    const resolved = resolvedStatusEntry(entry) ?? entry;
    const held = record(resolved.status_arr);
    const id = str(pick(entry, 'status_item_id')) || str(pick(record(held?.items), 'item_id'));
    if (held && record(held.items) && ('item_layer_types' in held || 'computed' in held
        || 'item_description' in (held.items as RecordValue))) return resolved;
    const value = id ? await (window as ObjectBridge).fetchAndCacheObject?.(id, { authoredCard: true }) : undefined;
    if (!value || !record(value.items)) throw new Error(`Status definition could not load: ${id}`);
    return { ...resolved, status_arr: value as StatusTemplateArr };
}

function label(row: unknown): { name: string; type: string } {
    const value = record(row);
    return {
        name: str(pick(record(value?.player_characters), 'player_name'))
            || str(pick(record(value?.encounter_creatures), 'encounter_creature_name'))
            || str(pick(record(value?.inventory), 'inventory_name'))
            || str(pick(record(value?.items), 'item_name')),
        type: str(pick(record(value?.items), 'item_type')),
    };
}

/** Resolve the template and one level of child templates without changing any cached instance. */
export async function hydrateStatusEntry(entry: StatusEntry): Promise<StatusEntry> {
    const children = record(entry.statuses)?.target;
    const resolved = await definition(entry);
    return Array.isArray(children)
        ? { ...resolved, statuses: { target: await Promise.all(children.map(child => definition(child as StatusEntry))) } }
        : resolved;
}

/** A JSON script survives AJAX innerHTML insertion; the normal post-inject scanner mounts it. */
export async function paintStatusMount(el: HTMLElement, cached?: StatusEntry): Promise<boolean> {
    const script = el.querySelector<HTMLScriptElement>(':scope > .ft-status-blob');
    const payload = script ? record(JSON.parse(script.textContent || '{}')) : undefined;
    const raw = record(payload?.entry) as StatusEntry | undefined;
    let entry = raw ?? cached;
    if (!entry) return false;
    if (raw) {
        const caster = label(payload?.caster), target = label(payload?.target), origin = label(payload?.origin);
        entry = { ...raw, status_caster_name: caster.name, status_caster_type: caster.type,
            status_target_name: target.name, status_target_type: target.type,
            status_origin_id_item_name: origin.name || str(pick(raw, 'status_origin_name')),
            status_origin_id_item_type: origin.type,
            statuses: { target: Array.isArray(payload?.children) ? payload.children : [] } };
    }
    entry = await hydrateStatusEntry(entry);
    if (!el.isConnected) return false;
    if (el.getAttribute('data-ft-status-mode') === 'form') {
        const ctx: EditCtx = {
            statusId: str(pick(entry, 'status_id')),
            campaignId: el.getAttribute('data-ft-campaign-id') || '',
            encounterId: el.getAttribute('data-ft-encounter-id') || '',
            casterId: el.getAttribute('data-ft-viewer-caster-id') || '',
        };
        renderStatusModifyForm(entry, ctx, el);
        return true;
    }
    return buildStatusEffectCard(entry, el);
}

const cardReads = new WeakMap<HTMLElement, object>();
/** Warm recognition still refreshes saved target context and child rows from the ordinary card URL. */
export async function paintCachedStatus(statusId: string, container: HTMLElement, viewerId?: string): Promise<void> {
    const claim = {};
    const before = container.innerHTML;
    cardReads.set(container, claim);
    const response = await fetch(`${BASE_URL}options/tables/open_table_item_inc.php`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data_in_1: statusId, caster_id: viewerId || '' }),
    });
    if (!response.ok) throw new Error(`Status context returned ${response.status}`);
    const html = await response.text();
    if (!container.isConnected || cardReads.get(container) !== claim || container.innerHTML !== before) return;
    container.innerHTML = html;
    const mount = container.querySelector<HTMLElement>('[data-ft-status-effect]');
    if (!mount) throw new Error('Status card response has no mount');
    if (await paintStatusMount(mount)) mount.setAttribute('data-ft-done', '1');
}
